// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { RelativePeriod } from '../../types';

interface ChartDataPoint {
    month: string;
    [key: string]: number | string | Record<string, unknown> | undefined;  // Extended to support byBroker breakdowns
}

interface MonthlyBreakdownChartProps {
    data: ChartDataPoint[];
    title: string;
    dataKey: string;
    color: string;
    variant?: 'currency' | 'count';
    unitLabel?: string;
    period?: RelativePeriod;  // Used to hide labels on congested 5Y/ALL views
    /** Optional per-point count shown after the value in the tooltip, e.g. "$14.2M · 23 loans". */
    countKey?: string;
    /** [singular, plural] noun for countKey. Defaults to ['item', 'items']. */
    countUnit?: [string, string];
}

const MonthlyBreakdownChart: React.FC<MonthlyBreakdownChartProps> = ({
    data,
    title,
    dataKey,
    color,
    variant = 'currency',
    unitLabel,
    period,
    countKey,
    countUnit = ['item', 'items'],
}) => {
    // Hide data point labels for 2Y+ periods (too congested with 24+ months)
    // Tooltip on hover still works
    const showLabels = period !== '2Y' && period !== '5Y' && period !== 'ALL';
    const gradientId = `gradient-${title.replace(/\s+/g, '-').toLowerCase()}`;

    // Format currency with K/M/B suffixes
    const formatCurrencyCompact = (value: number): string => {
        if (!Number.isFinite(value)) return '$0';
        const absValue = Math.abs(value);
        if (absValue >= 1_000_000_000) {
            return `$${(value / 1_000_000_000).toFixed(1)}B`;
        } else if (absValue >= 1_000_000) {
            return `$${(value / 1_000_000).toFixed(1)}M`;
        } else if (absValue >= 1_000) {
            return `$${(value / 1_000).toFixed(0)}K`;
        }
        return `$${value.toFixed(0)}`;
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const value = typeof payload[0].value === 'number' ? payload[0].value : 0;
            const dataPoint = payload[0].payload;

            // Format label with abbreviated year if yearMonth is available (e.g., "Oct '24")
            let displayLabel = label;
            if (dataPoint?.yearMonth) {
                const [year, monthNum] = dataPoint.yearMonth.split('-');
                const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
                const monthName = date.toLocaleString('default', { month: 'short' });
                displayLabel = `${monthName} ${year.slice(-2)}`;
            }

            const rawCount = countKey ? dataPoint?.[countKey] : undefined;
            const countSuffix = typeof rawCount === 'number' && Number.isFinite(rawCount)
                ? ` \u00b7 ${rawCount.toLocaleString('en-AU')} ${rawCount === 1 ? countUnit[0] : countUnit[1]}`
                : '';

            return (
                <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
                    <p className="font-bold mb-2">{displayLabel}</p>
                    <p style={{ color }}>
                        {variant === 'count'
                          ? `${title}: ${value.toLocaleString()}${unitLabel ? ` ${unitLabel}` : ''}`
                          : `${title}: $${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${countSuffix}`}
                    </p>
                </div>
            );
        }
        return null;
    };

    const CustomLabel = ({ x, y, value }: any) => {
        // ✅ FIX 1: ADDED TYPE GUARD TO PREVENT CRASH
        if (!Number.isFinite(value) || value === 0) return null; // Don't render label for 0

        return (
            <text x={x} y={y - 10} fill="#ffffff" textAnchor="middle" fontSize={11} fontWeight="500">
                {variant === 'count'
                  ? value.toLocaleString()
                  : formatCurrencyCompact(value)}
            </text>
        );
    };

    return (
        <div className="bg-gray-800 p-6 rounded-lg shadow-lg mt-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" 
                 style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
            />
            
            <h3 className="font-bold text-lg mb-4 text-white relative z-10">{title}</h3>
            <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data} margin={{ top: 25, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={color} stopOpacity={0.1}/>
                        </linearGradient>
                    </defs>
                    <XAxis dataKey="month" stroke="#8892b0" fontSize={12} />
                    <YAxis
                        stroke="#8892b0"
                        fontSize={12}
                        // ✅ FIX 2: ADDED TYPE GUARD FOR ROBUSTNESS + K/M/B formatting
                        tickFormatter={(value: number) => {
                            if (!Number.isFinite(value)) return '$0';
                            return variant === 'count' ? value.toLocaleString() : formatCurrencyCompact(value);
                        }}
                    />
                    <CartesianGrid strokeDasharray="3 3" stroke="#344155" opacity={0.3} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                        type="monotone"
                        dataKey={dataKey}
                        stroke={color}
                        strokeWidth={2.5}
                        fill={`url(#${gradientId})`}
                        dot={{ r: 2, fill: color }}
                        activeDot={{ r: 4, fill: color }}
                        label={showLabels ? <CustomLabel /> : undefined}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MonthlyBreakdownChart;